import { useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Box,
  Grid,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Button,
  Tooltip,
  useTheme
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";

import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cardRegistry } from "./cardRegistry";

export const MarkdownLink = ({ href, children }) => {
  const history = useHistory();
  const isInternal = href?.startsWith("/");

  const handleClick = (e) => {
    e.preventDefault();
    history.push(href);
  };

  if (isInternal) {
    return (
      <a
        href={href}
        onClick={handleClick}
        style={{
          color: "inherit",
          textDecoration: "underline",
          cursor: "pointer"
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "inherit", textDecoration: "underline" }}
    >
      {children}
    </a>
  );
};

export const markdownComponents = {
  p: ({ children }) => (
    <Typography variant="body2" color="inherit" paragraph>
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography variant="subtitle1" color="inherit">
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle1" color="inherit">
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle2" color="inherit">
      {children}
    </Typography>
  ),
  strong: ({ children }) => <strong>{children}</strong>,
  ul: ({ children }) => (
    <ul style={{ margin: "8px 0", paddingLeft: 22 }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: "8px 0", paddingLeft: 22 }}>{children}</ol>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: 8, paddingLeft: 4 }}>
      <Typography variant="body2" color="inherit" component="span">
        {children}
      </Typography>
    </li>
  ),
  a: MarkdownLink
};

export const UserMessage = ({ content, originTabBadge }) => {
  const { palette } = useTheme();
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end">
      {originTabBadge && (
        <Box mb={0.5} display="flex" justifyContent="flex-end">
          {originTabBadge}
        </Box>
      )}
      <Box
        px={2}
        py={1}
        style={{
          backgroundColor: palette.secondary.main,
          color: palette.common.white,
          borderRadius: "8px",
          maxWidth: "85%"
        }}
      >
        <Typography variant="body2" color="inherit">
          {content}
        </Typography>
      </Box>
    </Box>
  );
};

const NegativeFeedbackForm = ({ onSubmit, onCancel }) => {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(comment);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        variant="outlined"
        placeholder="Tell us what was wrong (optional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={handleKeyDown}
        multiline
        maxRows={3}
        disabled={submitting}
      />
      <Box display="flex" justifyContent="flex-end" style={{ gap: 4 }}>
        <Button size="small" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Sending..." : "Submit"}
        </Button>
      </Box>
    </Box>
  );
};

export const AssistantMessage = ({
  content,
  onRate,
  existingRating,
  cards,
  originTabBadge
}) => {
  const { palette } = useTheme();
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  const handleNegativeClick = () => setShowFeedbackForm(true);
  const handleSubmitNegative = async (comment) => {
    await onRate("negative", comment);
    setShowFeedbackForm(false);
  };

  const renderableCards = (cards || []).filter(
    (card) => card && cardRegistry[card.type]
  );

  return (
    <Box display="flex" justifyContent="flex-start">
      <Box
        display="flex"
        flexDirection="column"
        style={{
          maxWidth: "85%",
          gap: 4
        }}
      >
        {originTabBadge && (
          <Box mb={0.5} display="flex" justifyContent="flex-start">
            {originTabBadge}
          </Box>
        )}
        <Box
          style={{
            backgroundColor: palette.backgroundGray.dark,
            color: palette.text.primary,
            borderRadius: "8px"
          }}
          sx={{
            px: 2,
            py: 1.5,
            "& .MuiTypography-root": { lineHeight: 1.6 },
            "& p": { my: 1 },
            "& > :first-of-type": { mt: 0 },
            "& > :last-child": { mb: 0 }
          }}
        >
          <Markdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
            {content}
          </Markdown>
        </Box>
        {renderableCards.map((card, idx) => {
          const CardComponent = cardRegistry[card.type];
          return (
            <Box key={`card-${idx}`} mt={1}>
              <CardComponent {...(card.props || {})} />
            </Box>
          );
        })}
        {onRate && !showFeedbackForm && (
          <Box display="flex">
            <Tooltip title="Good response">
              <span>
                <IconButton
                  size="small"
                  disabled={!!existingRating}
                  onClick={() => onRate("positive")}
                >
                  {existingRating === "positive" ? (
                    <ThumbUpIcon fontSize="small" color="primary" />
                  ) : (
                    <ThumbUpIcon fontSize="small" color="disabled" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Bad response">
              <span>
                <IconButton
                  size="small"
                  disabled={!!existingRating}
                  onClick={handleNegativeClick}
                >
                  {existingRating === "negative" ? (
                    <ThumbDownIcon fontSize="small" color="error" />
                  ) : (
                    <ThumbDownIcon fontSize="small" color="disabled" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
        {onRate && showFeedbackForm && (
          <NegativeFeedbackForm
            onSubmit={handleSubmitNegative}
            onCancel={() => setShowFeedbackForm(false)}
          />
        )}
      </Box>
    </Box>
  );
};

export const ThinkingIndicator = () => {
  const { palette } = useTheme();
  return (
    <Box display="flex" justifyContent="flex-start">
      <Box
        px={2}
        py={1}
        style={{
          backgroundColor: palette.backgroundGray.main,
          borderRadius: "8px"
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <CircularProgress size={14} />
          </Grid>
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              Thinking...
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
